import moment, { MomentInput } from "moment";
import UrlFactory from "../prototypes/UrlFactory";
import { app } from "./AppModel";
import ProductModel from "./ProductModel";
import {
  IStore,
  IStoreOpeningHours,
  StoreTimeData,
} from "@src/interface/Store";
import cloneDeep from "lodash/cloneDeep";
import PaymentGroupCollection from "./PaymentGroupCollection";
import PaymentGroupModel from "./PaymentGroupModel";
import PaymentModeModel from "./PaymentModeModel";
import StockoutProductModel from "./StockoutProductModel";
import LocalStorageModel from "../prototypes/LocalStorageModel";
import DatetimeRound from "datetime-round";
import type { ICartDeliveryOptionsVAE } from "@src/backbone/model/CartModel";
import { urlWithSyncVersion } from "@src/backbone/prototypes/ObjectPrototype";
import { IDefaultTheme } from "design-system/src/Themes/defaultTheme";
import ApiRequest from "@src/backbone/prototypes/ApiRequest";
import StoreContextModel, {
  CatalogCategory,
} from "@src/backbone/model/StoreContextModel";
import momentFormats from "@src/backbone/prototypes/momentFormats";
import {
  EStoreOpenCode,
  StoreOpenCodeColors,
} from "@src/backbone/interface/EStoreOpenCode";
import {
  applyPrefetchToStoreContextModel,
  ensureStoreContextFetch,
  markStoreContextSynced,
} from "@src/lib/LocationFirst/storeContext";

declare global {
  interface Window {
    google: any;
  }
}

export type StoreAttribute = keyof IStore;
export enum EPriceType {
  TTC_VAE = "ttc_vae",
  TTC = "ttc",
  STRIKETHROUGH_PRICE = "strikethrough_price",
  HT = "ht",
}
export type Period = "am" | "pm";

export enum ENextOpeningType {
  BOOLEAN = "boolean",
  CODE = "code",
  TIME = "time",
  FROM_NOW = "fromNow",
  COLOR = "color",
  ALL = "all",
}

export type NextOpening = {
  code: EStoreOpenCode;
  label: string;
  secondaryLabel?: string;
  open: boolean;
  color: keyof IDefaultTheme["color"];
  time: string;
};

export enum EStoreClosureTimemode {
  Ponctual = "ponct",
  Periodic = "periodique",
}
export interface IStoreClosure {
  id_store?: string;
  closed: boolean;
  timemode?: EStoreClosureTimemode;
  from?: string;
  to?: string;
  message?: string;
  days?: {
    day_number: 0 | 1 | 2 | 3 | 4 | 5 | 6;
    time_deb_am: string;
    time_end_am: string;
    is_closed_am: boolean;
    time_deb_pm: string;
    time_end_pm: string;
    is_closed_pm: boolean;
  }[];
  next_date?: string;
}

//
export type GeoJSONSourceOptionsData =
  | GeoJSON.Feature<GeoJSON.Geometry>
  | GeoJSON.FeatureCollection<GeoJSON.Geometry>
  | GeoJSON.Geometry
  | string
  | undefined;

export interface IProductModifier {
  id_product: string;
  isStockout?: boolean;
  // legacy price
  price_ttc?: string;
  price_ttc_vae?: string;
  id_macaron?: string;
  // meiji price
  price?: number;
  strikethrough_price?: number;
  available?: boolean;
  sidedishes?: {
    id_cross_selling: string;
    products: {
      id_product: string;
      price: number;
    }[];
  }[];
}

//
// Model Store
//

const _TRANSLATION_CONTEXT_STORES_ = "Stores",
  _MIN_DIFFER_DELAY_ = 45; // délai minimum en différé par défaut, en minutes, par rapport à maintenant;

function PadSeconds(hhmm?: string) {
  hhmm = hhmm ? hhmm : "00:00"; // exception, si les données sont invalides, on gère un cas par défaut
  return hhmm.padEnd(6, ":").padEnd(8, "0");
}

function isBeforeNow(d: Date) {
  return moment(d).diff(moment()) < 0;
}

function minDifferDelay() {
  // délai minimum en différé, en ms, par rapport à maintenant;
  return app.c<number>("_MIN_DIFFER_DELAY_", _MIN_DIFFER_DELAY_) * 60000;
}

class StoreModel extends LocalStorageModel {
  name = "StoreModel";
  idAttribute = "id_store";
  route = "apiStoreModel";
  collections = undefined;
  remoteCache = "no-cache";
  messages: any;
  paymentGroupCollection: any;
  stockoutProduct: any;
  avisVerifie: any;
  _fetchFailed?: boolean;
  _storeContextModel?: StoreContextModel;
  _pendingStoreContextModel?: StoreContextModel;
  _storeContextModelTimeout?: NodeJS.Timeout;
  _storeContextRetried?: boolean;

  defaults() {
    return {
      name: "",
      active: true,
      comming_soon: false,
      sale_on_web: true,
      accept_shipping: true,
      accept_vae: true,
      template: "single-store",
      type_shop: 0,
      google_cid: undefined,
      breadcrumb: [],
      benefit: {
        primary: [],
        secondary: [],
      },
      closure: {
        closed: false,
      },
    };
  }

  initialize(attributes?: any, options?: any) {
    // ObjectModel constructor heritage
    super.initialize.apply(this, [attributes, options]);

    //
    if (!this.get(this.idAttribute as StoreAttribute)) {
      this.attributes[this.idAttribute as StoreAttribute] = this.get(
        "id"
      ) as any;
    }
  }

  /*getTemplate:function(){
              var template = this.get('template');
              if(template == 'travel-retail'){
                  template = 'corner';
              }
             return require('template!blocks/stores/corner'+template)
          }*/
  hasStoreContext() {
    const ctx = this._storeContextModel;
    if (!ctx) return false;
    const products = ctx.get("products");
    return Array.isArray(products) && products.length > 0;
  }

  // Catalogue dynamique résolu côté back (ordre/filtrage des items par boutique, mode et temporalité).
  // Disponible uniquement en mode MEIJI, après le fetch du storeContext.
  getCatalog(): CatalogCategory[] | undefined {
    return this._storeContextModel?.get("catalog");
  }

  getProductPrice(product: ProductModel, type: EPriceType = EPriceType.TTC) {
    const productModifier = this.getProduct(product);
    if (type === EPriceType.STRIKETHROUGH_PRICE) {
      return productModifier?.strikethrough_price || 0;
    } else {
      return productModifier?.price || 0;
    }
  }

  getProduct(product: string | ProductModel) {
    let productModifier: IProductModifier | undefined,
      store = this,
      id_product = typeof product === "string" ? product : `${product.id}`,
      storeContext = store._storeContextModel;
    if (storeContext) {
      productModifier = storeContext.getProduct(id_product);
    }
    if (productModifier) {
      productModifier.isStockout = store
        .getStockoutProduct()
        .isStockout(id_product);
    }
    return productModifier;
  }

  getName() {
    return this.get("name");
  }

  getFullName() {
    // nom du shop avec Sushi Shop devant (ou sa variante localisée)
    return app.getShopName() + " " + this.getName();
  }

  // getTypeName(plural?: boolean) {
  //   const shopName = app.getShopName(),
  //     type = this.get("type_shop");
  //   let typeName = shopName;
  //   if (type && type > 0) {
  //     // si le type n'est pas celui par défaut
  //     var TYPES_LABEL = ["Corner", "Travel retail"],
  //       label = TYPES_LABEL[type - 1] + (plural ? "s" : "");
  //     typeName += " " + app.t(label, "StoreType");
  //   }
  //   return typeName;
  // }

  // getTypeId() {
  //   var type = "store",
  //     type_shop = this.get("type_shop");
  //   switch (type_shop) {
  //     case 1:
  //       type = "corner";
  //       break;
  //     case 2:
  //       type = "travel-retail";
  //       break;
  //   }
  //   return type;
  // }

  // getTypeDiscoverLabel() {
  //   var model = this,
  //     label = "Découvrir ";
  //   switch (model.get("type_shop")) {
  //     case 2:
  //       label += "l'offre travel retail"; //
  //       break;
  //     case 1:
  //       label += "l'offre corner";
  //       break;
  //     default:
  //       label += "le shop";
  //   }
  //   return app.t(label, _TRANSLATION_CONTEXT_STORES_);
  // }

  // getServices() {
  //   // utilisé sur le template Boutique
  //   return app.getStoreServices().filterByType(this.get("type_shop"));
  // }
  //
  // isSalesOnWeb() {
  //   // retourne true si la boutique est disponible
  //   // en fonction du mode de livraison
  //   return this.get("sale_on_web");
  // }
  //
  // isAvailable(VAE: boolean) {
  //   // retourne true si la boutique est disponible
  //   // en fonction du mode de livraison
  //   const isVAE = VAE === true,
  //     store = this,
  //     comming_soon = store.get("comming_soon"),
  //     active = store.get("active"),
  //     sale_on_web = store.get("sale_on_web"),
  //     acceptDeliveryMode = isVAE
  //       ? store.get("accept_vae")
  //       : store.get("accept_shipping");
  //   return !comming_soon && active && sale_on_web && acceptDeliveryMode;
  // }

  // isVae() {
  //   return this.get("accept_vae");
  // }
  //
  // getPhone() {
  //   return this.get("phone");
  // }
  //
  // getPhonePricing() {
  //   return this.get("phone_pricing");
  // }

  getLatitude() {
    return parseFloat(this.get("gmap_latitude") || "");
  }

  getLongitude() {
    return parseFloat(this.get("gmap_longitude") || "");
  }

  getLatLng() {
    return {
      lat: this.getLatitude(),
      lng: this.getLongitude(),
    };
  }

  // getLngLat() {
  //   // alias for mapbox
  //   return [this.getLongitude(), this.getLatitude()];
  // }

  // getUrl() {
  //   return this.isInternational()
  //     ? this.get("url") // boutiques des autres pays
  //     : UrlFactory.getUrl("store", {
  //         store_rewrite: this.get("link_rewrite"),
  //       }); // boutiques du pays
  // }

  // getAboutUrl() {
  //   // lien vers la page de la typologie de groupe concernée
  //   // bien créer des Widget Group
  //   // avec les identifiants "about_corners", "about_travel_retails" et "about_shops"
  //   const type_shop = this.get("type_shop"),
  //     group_slug =
  //       "about_" +
  //       (type_shop === 1
  //         ? "corners"
  //         : type_shop === 2
  //         ? "travel_retails"
  //         : "shops"),
  //     widgetGroup = app.getWidgetGroup(group_slug);
  //   return widgetGroup ? widgetGroup.getUrl() : this.getUrl();
  // }

  // getMessages() {
  //   // retourne la collection des messages par shop, visible sur la page boutique
  //   let messageCollection = this.messages;
  //   if (!messageCollection) {
  //     messageCollection = new StoreMessageCollection(this.id + "");
  //   }
  //   return messageCollection;
  // }

  // requestDistance(type: string = "distance_google", force?: boolean) {
  //   const model = this;
  //   let distance: number,
  //     maps,
  //     latLng = app.get("latLng"),
  //     storeLatLng = model.getLatLng();
  //
  //   //
  //   function computeDistance(latLng: string) {
  //     try {
  //       maps = window.google.maps;
  //       storeLatLng = new maps.LatLng(storeLatLng);
  //       distance = maps.geometry.spherical.computeDistanceBetween(
  //         storeLatLng,
  //         latLng
  //       );
  //       model.set(type, distance);
  //       return distance;
  //     } catch (err) {
  //       // do nothing
  //     }
  //   }
  //
  //   if (!model.get(type) || force) {
  //     if (latLng) {
  //       return computeDistance(latLng);
  //     } else {
  //       model.listenToOnce(app, "geolocation", function (location) {
  //         computeDistance(location.latLng);
  //       });
  //       // app.requestGeolocation();
  //     }
  //   }
  //   return model.get(type);
  // }

  // hasDistance(type?: string) {
  //   type = !type ? "_actual" : type; // gère 2 types de distances
  //
  //   let distance = this.get(("distance" + type) as StoreAttribute);
  //   // une basée sur la recherche, l'autre sur la position actuelle
  //   if (!distance) {
  //     distance = this.requestDistance();
  //   }
  //   return distance ? true : false;
  // }

  // getDistance(
  //   type: string = "distance_actual",
  //   fixed: number = 0
  // ): number | undefined {
  //   let distance: number = this.get(type as any) || NaN;
  //   if (!distance && type === "distance_google") {
  //     distance = this.requestDistance(type);
  //   }
  //   return distance >= 0
  //     ? parseFloat((distance / 1000).toFixed(fixed >= 0 ? fixed : 1))
  //     : undefined;
  // }

  // formatDistance(type?: string) {
  //   const d = this.getDistance(type);
  //   return d ? d + " km" : "";
  // }

  // getDistricts() {
  //   return this.get("districts") || [];
  // }

  // hasDistricts() {
  //   return this.getDistricts().length > 0;
  // }

  // isAllowjackpot() {
  //   return this.get("allow_use_jackpot");
  // }

  // getGMBUrl() {
  //   const google_cid = this.get("google_cid");
  //   return google_cid ? "https://www.google.com/maps?cid=" + google_cid : "";
  // }

  getAddress(n: "" | "2" = "") {
    return (this.get(`address${n}`) || "") as string;
  }

  getCity() {
    return this.get("city");
  }

  // getPostcodes(): string[] {
  //   return this.get("postcodes") || [];
  // }

  // isPostcode(id_postcode: string | number) {
  //   const postcode = this.getPostcode();
  //   return !!postcode && postcode.id === id_postcode;
  // }

  // getDeliveryAreas() {
  //   return this.get("deliveryAreas");
  // }

  // getDeliveryAreasBbox() {
  //   return this.get("deliveryAreasBbox");
  // }

  // getPostcode() {
  //   const postCode = this.get("postcode");
  //   return postCode ? app.getPostcodeByValue(postCode) : undefined;
  // }

  formatPostcode() {
    return this.get("postcode");
  }

  getModifiers() {
    return this.get("modifiers");
  }

  // getAutocompletePattern() {
  //   const store = this,
  //     autocompletePatternAttr: StoreAttribute = "autocompletePattern";
  //   let pattern: string = store.get(autocompletePatternAttr) || "";
  //   if (!pattern) {
  //     let patternArray = _([Simplification(store.getName() || "")]);
  //     const postcodes = store.getPostcodes();
  //     // on ajoute les données code postal (ville et numéro)
  //     _(postcodes).each(function (id_postcode) {
  //       var postcode = app.getPostcode(id_postcode);
  //       if (postcode) {
  //         // si postcode existe
  //         var localities = postcode.getLocalitySimplification(false);
  //         patternArray = _(
  //           patternArray.union(patternArray.union(localities), [
  //             Simplification(postcode.getPostcode()),
  //           ])
  //         );
  //       }
  //     });
  //     pattern = patternArray.toArray().join(" ");
  //     store.set(autocompletePatternAttr, pattern);
  //   }
  //   return pattern;
  // }

  //
  // formatAddress(withPostcode?: boolean) {
  //   const store = this,
  //     a = store.getAddress(),
  //     c = store.getCity();
  //   return a + ", " + (withPostcode ? store.formatPostcode() : "") + " " + c;
  // }

  // vérifie que le ou les postcode est associé à la boutique
  // intersectsOneOfThosePostcodes(
  //   postcodes: string | string[],
  //   datetime: any
  // ): string[] {
  //   // permet d'utiliser la fonction avec un postcode unique ou un tableau
  //   if (!_.isArray(postcodes)) {
  //     postcodes = [postcodes];
  //   }
  //   //
  //   const store = this;
  //   let intersections: string[] = _.intersection(
  //     postcodes,
  //     store.getPostcodes()
  //   );
  //   if (intersections.length > 0) {
  //     // si des postcodes sont disponibles
  //     // on vérifie par rapport dans le créneau horaire
  //     const zones = store.getZonesDatetime(datetime);
  //     //
  //     // si il y a des créneaux horaires
  //     //
  //     const zonesPostcodes = _(zones)
  //       .map(function (zone) {
  //         const id_district = zone.id_district;
  //         if (id_district) {
  //           // si il y a des quartiers, on vérifie le code postal du quartier
  //           //
  //           const districtPostcode = app.getPostcodeByDistrict(id_district);
  //           //
  //           if (districtPostcode) {
  //             // si on a un postcode
  //             return districtPostcode.id as string;
  //           } else {
  //             // cas théoriquement non souhaitable
  //             // données à nettoyer
  //             return "";
  //           }
  //         }
  //         return zone.id_postcode || "";
  //       })
  //       .filter((id_postcode) => id_postcode !== "");
  //     intersections = _.intersection(intersections, zonesPostcodes);
  //   }
  //   return intersections;
  // }

  isDatetimeAm(isVAE: boolean, targetDatetime?: Date): boolean {
    const periods = this.getTimePeriods(isVAE, targetDatetime);
    return periods ? moment(targetDatetime).diff(periods.am.end) < 0 : false;
  }

  // isPeriodAvailable(
  //   isVAE: boolean,
  //   period: "am" | "pm",
  //   targetDatetime?: Date
  // ): boolean {
  //   const timePeriod = this.getTimePeriods(isVAE, targetDatetime);
  //   const periodTime = timePeriod ? timePeriod[period] : "";
  //   return periodTime ? !(periodTime.passed || periodTime.closed) : false;
  // }
  getClosure() {
    return this.get("closure");
  }

  isExceptionalClosure() {
    const closure: IStoreClosure = this.getClosure();
    return closure && closure.closed;
  }

  getExceptionalClosureMessages() {
    const closure: IStoreClosure = this.getClosure();
    const { from, to, timemode, message, days = [] } = closure;

    let message_detail = message;
    let message_period = "";
    let days_impacted = [];
    let is_ponct = timemode === EStoreClosureTimemode.Ponctual;

    if (is_ponct) {
      // fermeture ponctuelle
      let i;
      const start = moment(from),
        finish = moment(to),
        startTime = start.format("LT"),
        startDay = start.format("ll"),
        finishTime = finish.format("LT"),
        finishDay = finish.format("ll"),
        start0000 = start.format("Hm") === "00",
        finish2359 = finish.format("Hm") === "2359",
        start_day = start.day(),
        finish_day = finish.day(),
        rolling_sunday = finish_day < start_day, // si le jour de réouverture est avant celui de fermeture
        sameDay = start.isSame(finish, "day");
      for (i = start_day; i <= (rolling_sunday ? 6 : finish_day); i++) {
        days_impacted.push(i);
      }
      if (rolling_sunday) {
        // en cas de passage au chevauchement du dimanche
        for (i = 0; i <= finish_day; i++) {
          days_impacted.push(i);
        }
      }
      if (sameDay) {
        if (start0000 && finish2359) {
          message_period = app.t("le %s", "Datetime", [startDay]);
        } else if (start0000) {
          message_period = app.t("le %s jusqu’à %s", "Datetime", [
            startDay,
            finishTime,
          ]);
        } else if (finish2359) {
          message_period = app.t("le %s à partir de %s", "Datetime", [
            startDay,
            startTime,
          ]);
        } else {
          message_period = app.t("le %s de %s à %s", "Datetime", [
            startDay,
            startTime,
            finishTime,
          ]);
        }
      } else {
        if (start0000 && finish2359) {
          message_period = app.t("du %s au %s", "Datetime", [
            startDay,
            finishDay,
          ]);
        } else {
          message_period = app.t("du %s à %s au %s à %s", "Datetime", [
            startDay,
            startTime,
            finishDay,
            finishTime,
          ]);
        }
      }
    } else {
      // fermeture récurrente
      let message_period_parts: string[] = [];
      days.forEach(function (d) {
        var dayMoment = moment(),
          day = d.day_number,
          message_period_part: string,
          closed_am = true;
        dayMoment.day(day);
        days_impacted.push(day);
        message_period_part = "";
        ["am", "pm"].forEach(function (period) {
          var is_am = period === "am",
            // @ts-ignore
            time_deb = d["time_deb_" + period],
            // @ts-ignore
            time_end = d["time_end_" + period],
            time_deb_0000 = time_deb === "00:00",
            // @ts-ignore
            is_closed = d["is_closed_" + period],
            closed_am_before_pm =
              !is_am && closed_am ? " " + app.t("et", "UI") + " " : "";
          if (is_closed) {
            if (d["is_closed_am"] && d["is_closed_pm"]) {
              message_period_part = app.t("toute la journée", "Datetime");
            } else {
              message_period_part +=
                closed_am_before_pm +
                app.t(is_am ? "le matin" : "le soir", "Datetime");
            }
          } else if (time_deb && time_end) {
            if (time_deb_0000) {
              message_period_part +=
                closed_am_before_pm +
                app.t("jusqu’à %s", "Datetime", [time_end]);
            } else {
              message_period_part +=
                closed_am_before_pm +
                app.t("de %s à %s", "Datetime", [time_deb, time_end]);
            }
          } else {
            closed_am = false;
          }
        });
        message_period_part =
          app.t(
            "les %s (jour de la semaine)",
            "Datetime",
            dayMoment.format("dddd")
          ) +
          " " +
          message_period_part;
        message_period_parts.push(message_period_part);
      });
      message_period = message_period_parts.join(", ");
    }

    return {
      message_period,
      message_detail,
    };
  }

  // getExceptionalClosureMessage() {
  //   const closure: IStoreClosure = this.getClosure();
  //   return closure?.message || "";
  // }

  // getNextOpeningHours<T extends ENextOpeningType = ENextOpeningType>(
  //   type: T = ENextOpeningType.CODE as T
  // ): T extends ENextOpeningType.ALL
  //   ? NextOpening
  //   : string | boolean | EStoreOpenCode {
  //   const store = this;
  //   let openingValue: string,
  //     openingCode: EStoreOpenCode, // 1 signifie ouvert
  //     openingState: boolean;
  //   const openingHours = store.getOpeningHours();
  //
  //   if (openingHours && openingHours.length > 0) {
  //     const todayOpeningHours = openingHours.find((hours) => hours.today);
  //     if (todayOpeningHours) {
  //       const {
  //         is_closed_am,
  //         is_closed_pm,
  //         time_opening_am,
  //         time_opening_pm,
  //         time_closing_am,
  //         time_closing_pm,
  //       } = todayOpeningHours;
  //
  //       const now = moment();
  //       const openingAM = moment(time_opening_am, "HH:mm");
  //       const closingAM = moment(time_closing_am, "HH:mm");
  //       const openingPM = moment(time_opening_pm, "HH:mm");
  //       const closingPM = moment(time_closing_pm, "HH:mm");
  //
  //       const isOpenAM =
  //         is_closed_am === "0" && now.isBetween(openingAM, closingAM);
  //       const isOpenPM =
  //         is_closed_pm === "0" && now.isBetween(openingPM, closingPM);
  //
  //       if (isOpenAM || isOpenPM) {
  //         openingState = true;
  //         openingCode = EStoreOpenCode.OPEN;
  //         openingValue = app.t("Ouvert", _TRANSLATION_CONTEXT_STORES_);
  //       } else {
  //         openingState = false;
  //         openingCode = EStoreOpenCode.CLOSED;
  //         const openingDiffAM = openingAM.diff(now);
  //         const openingDiffPM = openingPM.diff(now);
  //
  //         if (
  //           (openingDiffAM > 0 && openingDiffAM < 60 * 60 * 1000) ||
  //           (openingDiffPM > 0 && openingDiffPM < 60 * 60 * 1000)
  //         ) {
  //           openingCode = EStoreOpenCode.OPEN_SOON;
  //           let time;
  //           if (openingDiffAM < 60 * 60 * 1000) {
  //             time = openingAM.fromNow();
  //           } else {
  //             time = openingPM.fromNow();
  //           }
  //           openingValue =
  //             app.t("Ouvre", _TRANSLATION_CONTEXT_STORES_) + " " + time;
  //         } else {
  //           openingValue = app.t("Fermé", _TRANSLATION_CONTEXT_STORES_);
  //         }
  //       }
  //     } else {
  //       openingState = false;
  //       openingCode = EStoreOpenCode.CLOSED;
  //       openingValue = app.t("Fermé", _TRANSLATION_CONTEXT_STORES_);
  //     }
  //   } else {
  //     openingState = false;
  //     openingCode = EStoreOpenCode.CLOSED;
  //     openingValue = app.t("Fermé", _TRANSLATION_CONTEXT_STORES_);
  //   }
  //
  //   if (type === ENextOpeningType.ALL) {
  //     return {
  //       code: openingCode,
  //       label: openingValue,
  //       open: openingState,
  //       color: StoreOpenCodeColors[openingCode],
  //     } as T extends ENextOpeningType.ALL ? NextOpening : never;
  //   }
  //   return (
  //     type === ENextOpeningType.BOOLEAN
  //       ? openingState
  //       : type === ENextOpeningType.CODE
  //       ? openingCode
  //       : type === ENextOpeningType.COLOR
  //       ? StoreOpenCodeColors[openingCode]
  //       : openingValue
  //   ) as T extends ENextOpeningType.ALL
  //     ? never
  //     : string | boolean | EStoreOpenCode;
  // }

  getNextOpening<T extends ENextOpeningType = ENextOpeningType>(
    type: T = ENextOpeningType.CODE as T,
    useOpeningHours: boolean = false,
    closureFetching: boolean = true
  ): T extends ENextOpeningType.ALL
    ? NextOpening
    : string | boolean | EStoreOpenCode {
    //type = _.isUndefined(type) ? 'fromNow':type;
    //
    const store = this,
      closure = this?.getClosure(),
      storeOpening = store.findAvailableDatetime(true, useOpeningHours);
    let openingPrimaryLabel: string,
      openingSecondaryLabel: string | undefined,
      openingCode: EStoreOpenCode, // 1 signifie ouvert
      openingState: boolean,
      openingTime: string = "";

    if (closureFetching && !closure) {
      openingState = false;
      openingCode = EStoreOpenCode.FETCHING;
      openingPrimaryLabel = app.t("Veuillez patienter", "UI");
    } else if (store.isExceptionalClosure()) {
      openingCode = EStoreOpenCode.EXCEPTIONAL_CLOSURE;
      openingPrimaryLabel = app.t(
        "Exceptionnellement fermé",
        _TRANSLATION_CONTEXT_STORES_
      );
      openingState = false;
    }
    // if corner case
    else if (storeOpening.closed) {
      openingCode = EStoreOpenCode.CLOSED;
      openingPrimaryLabel = app.t("Fermé", _TRANSLATION_CONTEXT_STORES_);
      openingState = false;
    } else if (storeOpening.opened && storeOpening.closeSoon) {
      openingCode = EStoreOpenCode.CLOSED_SOON;
      openingPrimaryLabel = app.t(
        "Ferme bientôt",
        _TRANSLATION_CONTEXT_STORES_
      );
      openingState = true;
      const { closeMoment } = storeOpening;
      openingSecondaryLabel = app.t(
        "à %s",
        "Datetime",
        closeMoment?.format("LT") || ""
      );
    } else if (storeOpening.opened) {
      openingCode = EStoreOpenCode.OPEN;
      openingPrimaryLabel = app.t("Ouvert", _TRANSLATION_CONTEXT_STORES_);
      openingState = true;
    } else {
      openingState = false;
      openingCode = openingState ? EStoreOpenCode.OPEN : EStoreOpenCode.CLOSED;
      const openingMoment = storeOpening.openMoment;
      const { openSoon = false, openDelay = 0 } = storeOpening;
      const openingIsToday = openingMoment?.isSame(moment(), "day");
      const openingIsInMax7Days =
        openDelay > 0 && openDelay <= 7 * 24 * 60 * 60 * 1000; // dans les 7 prochains jours
      switch (type) {
        case ENextOpeningType.TIME:
          openingTime = openingMoment?.format("LLLL") || "";
          break;
        default: //
          openingTime =
            (openSoon || openingIsToday
              ? openingMoment?.format("LT") // à XX:XX
              : openingMoment?.calendar(
                  momentFormats.getFormats({ ignoreService: true })
                )) || "";
          break;
        // eslint-disable-next-line no-fallthrough
        case ENextOpeningType.FROM_NOW:
          openingTime = openingMoment?.fromNow() || "";
          break;
      }
      if (openSoon) {
        openingCode = EStoreOpenCode.OPEN_SOON;
        openingPrimaryLabel = app.t(
          "Ouvre bientôt",
          _TRANSLATION_CONTEXT_STORES_
        );
        openingSecondaryLabel = app.t("à %s", "Datetime", openingTime);
      } else {
        openingPrimaryLabel = app.t("Fermé", _TRANSLATION_CONTEXT_STORES_);
        if (openingIsToday) {
          openingSecondaryLabel = app.t("Ouvre à %s", "Datetime", openingTime);
        } else if (openingIsInMax7Days) {
          openingSecondaryLabel =
            app.t("Ouvre", _TRANSLATION_CONTEXT_STORES_) + " " + openingTime;
        }
      }
    }

    if (type === ENextOpeningType.ALL) {
      return {
        code: openingCode,
        label: openingPrimaryLabel,
        secondaryLabel: openingSecondaryLabel,
        open: openingState,
        time: openingTime,
        color: StoreOpenCodeColors[openingCode],
      } as T extends ENextOpeningType.ALL ? NextOpening : never;
    }
    return (
      type === ENextOpeningType.BOOLEAN
        ? openingState
        : type === ENextOpeningType.CODE
        ? openingCode
        : type === ENextOpeningType.COLOR
        ? StoreOpenCodeColors[openingCode]
        : openingPrimaryLabel
    ) as T extends ENextOpeningType.ALL
      ? never
      : string | boolean | EStoreOpenCode;
  }

  getOpeningHours() {
    const store = this,
      AM = "am",
      PM = "pm",
      TIME_FORMAT = "LT",
      DEFAULT_DAY = "2000-01-01 ",
      opening_hours: IStore["opening_hours"] = cloneDeep(
        store.get("opening_hours") || []
      ),
      startOfWeek = moment().weekday(0).day(); // différence dans le 1er jour de la semaine
    // par defaut, le jour 0  est le dimanche. En france c’est le lundi

    // NE MARCHE PAS
    for (let d = 0; d < startOfWeek; d++) {
      opening_hours.push(opening_hours[0]);
      opening_hours.shift();
    }

    // formate l'affichage d'un temps, via moment.js
    function FormatTime(time: string) {
      return moment(DEFAULT_DAY + time).format(TIME_FORMAT);
    }

    // TODO Gérer le premier jour de la semaine en fonction de la locale
    function FormatPeriod(period: Period, dayData: IStoreOpeningHours) {
      const is_closed =
        period === "am"
          ? dayData.is_closed_am === "1"
          : dayData.is_closed_pm === "1";
      let from,
        to,
        label = app.t(
          "fermé le " + (period === "am" ? "matin" : "soir"),
          _TRANSLATION_CONTEXT_STORES_
        );
      if (!is_closed) {
        const timeOpening =
          period === "am" ? dayData.time_opening_am : dayData.time_opening_pm;
        const timeClosing =
          period === "am" ? dayData.time_closing_am : dayData.time_closing_pm;
        from = timeOpening ? FormatTime(timeOpening) : "";
        to = timeClosing ? FormatTime(timeClosing) : "";
        label = from + " - " + to;
      }

      return { label: label };
    }

    // Gère l'affichage automatique pour le service continu
    function Continous(dayData: IStoreOpeningHours) {
      const amClosed = dayData.is_closed_am === "1",
        pmClosed = dayData.is_closed_pm === "1",
        isContinousClosed = amClosed && pmClosed;

      let isContinous: { label: string } | undefined;

      const canContiue =
        dayData.time_closing_am === dayData.time_opening_pm ||
        isContinousClosed;
      if (isContinousClosed) {
        isContinous = {
          label: app.t("fermé toute la journée", _TRANSLATION_CONTEXT_STORES_),
        };
      } else if (canContiue) {
        var label,
          from = dayData.time_opening_am
            ? FormatTime(dayData.time_opening_am)
            : "",
          to = dayData.time_closing_pm
            ? FormatTime(dayData.time_closing_pm)
            : "";
        label = from + " - " + to;
        isContinous = { label: label };
      }
      return isContinous;
    }

    return opening_hours.map((dayData, i) => {
      const clonedDayDate: IStoreOpeningHours = cloneDeep(dayData);
      const day = moment().weekday(i);
      return {
        day: day.day(),
        today: day.day() === new Date().getDay(),
        dayLabel: day.format("dddd"),
        continous: Continous(dayData),
        am: FormatPeriod(AM, dayData),
        pm: FormatPeriod(PM, dayData),
        ...clonedDayDate,
      };
    });
  }

  getTimePeriods(
    isVAE: boolean,
    targetDatetime?: Date,
    useOpeningHours: boolean = false, // useOpeningHours :
    // permet d'obtenir les horaires publics d'ouverture (opening_hours)
    // à la place des créneaux de vente en ligne
    forceDiffer?: boolean
  ) {
    let now,
      targetMoment,
      targetDay: number,
      targetData: StoreTimeData | undefined,
      targetOpeningHour: IStoreOpeningHours,
      times = this.get("time"),
      opening_hours = this.get("opening_hours"),
      opening_am: MomentInput,
      now_closing_am: MomentInput,
      closed_am: boolean,
      opening_pm: MomentInput,
      now_closing_pm: MomentInput,
      closed_pm: boolean,
      isNowAm,
      isNowPm,
      targetMomentDay; //1000 * 60 * 15; // + 45 minutes

    // default return, if store time is not fetched
    if (!times || !opening_hours) return;

    const timesV = isVAE ? times.vae : times.vad;

    // par défaut la date cible est maintenant
    if (!targetDatetime) {
      targetDatetime = new Date();
    }

    targetDay = targetDatetime.getDay();
    targetOpeningHour = opening_hours[targetDay];

    targetMoment = moment(targetDatetime);
    targetMomentDay = targetMoment.format("YYYY/MM/DD ");

    // am
    opening_am = targetOpeningHour.time_opening_am;
    now_closing_am = timesV.now
      ? PadSeconds(timesV.now[targetDay].time_end_am || "")
      : "";
    closed_am = targetOpeningHour.is_closed_am === "1";
    // pm
    opening_pm = targetOpeningHour.time_opening_pm;
    now_closing_pm = timesV.now
      ? PadSeconds(timesV.now[targetDay].time_end_pm || "")
      : "";
    closed_pm = targetOpeningHour.is_closed_pm === "1";

    function isNowInPeriod(am: boolean) {
      //
      // est-ce que la date cible utilise maintenant ou différer
      //
      var openingTodayPeriod = moment(am ? opening_am : opening_pm, "HH:mm:ss"),
        closingTodayPeriod = moment(
          am ? now_closing_am : now_closing_pm,
          "HH:mm:ss"
        ),
        closed = am ? closed_am : closed_pm;
      //

      //
      return (
        openingTodayPeriod.isBefore(targetDatetime) &&
        closingTodayPeriod.isAfter(targetDatetime) &&
        !closed
      );
    }

    function mapOpeningHoursToTimesData(
      dayOpeningHours: IStoreOpeningHours
    ): StoreTimeData {
      const {
        time_opening_am,
        time_closing_am,
        is_closed_am,
        time_opening_pm,
        time_closing_pm,
        is_closed_pm,
      } = dayOpeningHours;
      return {
        time_deb_am: time_opening_am,
        time_end_am: time_closing_am,
        is_closed_am,
        time_deb_pm: time_opening_pm,
        time_end_pm: time_closing_pm,
        is_closed_pm,
      };
    }

    isNowAm = isNowInPeriod(true);
    isNowPm = isNowInPeriod(false);
    now = isNowAm || isNowPm;
    const targetDataBaseList = useOpeningHours
      ? opening_hours.map(mapOpeningHoursToTimesData)
      : forceDiffer || !now
      ? timesV.differ
      : timesV.now;
    targetData =
      targetDataBaseList && targetDataBaseList[targetDay]
        ? targetDataBaseList[targetDay]
        : undefined;

    let // AM
      am_deb_date = new Date(
        targetMomentDay +
          PadSeconds(
            targetData && targetData.time_deb_am ? targetData.time_deb_am : ""
          )
      ),
      am_end_date = new Date(
        targetMomentDay +
          PadSeconds(
            targetData && targetData.time_end_am ? targetData.time_end_am : ""
          )
      ),
      am_now_closing_date = new Date(
        targetMomentDay + PadSeconds(now_closing_am)
      ),
      am_open_date = new Date(targetMomentDay + PadSeconds(opening_am || "")),
      am_closed = targetData ? targetData.is_closed_am === "1" : false,
      // PM
      pm_deb_date = new Date(
        targetMomentDay +
          PadSeconds(
            targetData && targetData.time_deb_pm ? targetData.time_deb_pm : ""
          )
      ),
      pm_end_date = new Date(
        targetMomentDay +
          PadSeconds(
            targetData && targetData.time_end_pm ? targetData.time_end_pm : ""
          )
      ),
      pm_now_closing_date = new Date(
        targetMomentDay + PadSeconds(now_closing_pm)
      ),
      pm_open_date = new Date(targetMomentDay + PadSeconds(opening_pm || "")),
      pm_closed = targetData ? targetData.is_closed_pm === "1" : false;

    const am = {
        closed: am_closed,
        passed: am_end_date ? isBeforeNow(am_end_date) : false,
        passedNow: am_now_closing_date
          ? isBeforeNow(am_now_closing_date)
          : false,
        deb: am_deb_date,
        end: am_end_date,
        closing: am_now_closing_date,
        open: am_open_date,
      },
      pm = {
        closed: pm_closed,
        passed: isBeforeNow(pm_end_date),
        passedNow: isBeforeNow(pm_now_closing_date),
        deb: pm_deb_date,
        end: pm_end_date,
        closing: pm_now_closing_date,
        open: pm_open_date,
      };
    return { am, pm };
  }

  findAvailableDatetime(
    isVAE: boolean,
    useOpeningHours: boolean = false, // usage des horaires d'ouverture plutôt que des créneaux
    targetDatetime?: Date,
    r?: number
  ): {
    closed: boolean;
    openDatetime?: any;
    openMoment?: moment.Moment;
    closeSoon?: boolean;
    closeMoment?: moment.Moment;
    openSoon?: boolean;
    openDelay?: number;
    opened?: boolean;
    moment?: moment.Moment;
    momentFormatted?: string;
    now?: number;
    diff?: number;
  } {
    //
    // cherche la meilleure date disponible pour la boutique en cours
    //

    // si la date est passée on se calle sur maintenant
    if (moment(targetDatetime).diff(moment()) < 0) {
      targetDatetime = new Date();
    }

    const store_closed = {
      closed: true,
    };

    const store = this;
    let targetMoment = moment(targetDatetime),
      openDatetime,
      debDatetime,
      endDatetime,
      continuousService = false,
      periods,
      conversion,
      now = -1; // différé

    if (!store.get("active")) {
      return store_closed;
    }

    // on arrondit au supérieur la date de destination à 15 minutes
    // targetMoment = targetMoment.second(0).ceil(15, "minutes");
    targetMoment = DatetimeRound(targetMoment.second(0), 15, "minutes", "ceil");
    targetDatetime = targetMoment.toDate();

    periods = store.getTimePeriods(isVAE, targetDatetime, useOpeningHours);

    function Recursion() {
      // TODO attention, risque de récursivité infinie, il faut investiguer sur ce c
      // essaie de trouver le prochain créneau, en ajoutant une heure
      if (r && r > 72) {
        return false;
      }
      const nextAvailableDatetime = targetDatetime
        ? store.findAvailableDatetime(
            isVAE,
            useOpeningHours,
            new Date(targetDatetime.getTime() + 3600 * 1000),
            r ? r + 1 : 1
          )
        : undefined;
      if (
        nextAvailableDatetime &&
        nextAvailableDatetime.closed &&
        r &&
        r >= 72
      ) {
        return false;
      }
      if (nextAvailableDatetime && nextAvailableDatetime.moment) {
        conversion = nextAvailableDatetime.moment.toDate();
        openDatetime = nextAvailableDatetime.openDatetime;
      }
      return true;
    }

    function ValidateMinDifferDelay(date: Date) {
      const mDate = moment(date);
      if (minDifferDelay() > mDate.diff(moment())) {
        const upDate = mDate.add(15, "minutes").toDate();
        date = ValidateMinDifferDelay(upDate);
      }
      return date;
    }

    if (!periods) {
      return store_closed;
    }
    if (
      !(periods.am.closed || periods.am.passed || periods.am.passedNow) &&
      targetMoment.diff(periods.am.end) <= 0
    ) {
      // si la boutique n'est pas fermée le midi,
      // et que la plage am est passée
      // et que l’horaire cible est avant la fin de l’ouverture

      debDatetime = periods.am.deb;
      openDatetime = periods.am.open;
      // service continu
      continuousService =
        !periods.pm.closed && periods.am.end >= periods.pm.deb;
      // en cas de service continu, on prend la fin pm
      endDatetime = continuousService ? periods.pm.end : periods.am.end;
      if (targetMoment.diff(debDatetime) <= 0) {
        // si on est avant l'ouverture
        conversion = ValidateMinDifferDelay(debDatetime);
      } else if (targetMoment.diff(periods.am.end) <= 0) {
        //
        conversion = moment.max(targetMoment, moment(debDatetime)).toDate();
        conversion = targetDatetime;
      } else {
      }
    } else if (
      !(periods.pm.closed || periods.pm.passed || periods.pm.passedNow)
    ) {
      debDatetime = periods.pm.deb;
      openDatetime = periods.pm.open;
      endDatetime = periods.pm.end;
      if (targetMoment.diff(debDatetime) <= 0) {
        conversion = ValidateMinDifferDelay(debDatetime);
      } else if (targetMoment.isAfter(periods.pm.end)) {
        // cas après 23h on essaie de passer au lendemain
        if (!Recursion()) {
          return store_closed;
        }
      } else {
        conversion = moment.max(targetMoment, moment(debDatetime)).toDate();
      }
    } else {
      // cas ou la boutique est fermée toute la journée
      // on essaie le lendemain
      if (!Recursion()) {
        return store_closed;
      }
    }
    const momentConversion = moment(conversion);
    //
    if (momentConversion.diff(moment()) <= 60000 * 30) {
      now = 1;
    } else if (
      DatetimeRound(moment(new Date()), 24, "hours", "floor").diff(
        DatetimeRound(momentConversion.clone(), 24, "hours", "floor")
      ) === 0
    ) {
      now = 0;
    }
    if (!openDatetime) {
      return store_closed;
    }
    const openMoment = moment(openDatetime),
      closeMoment = moment(endDatetime),
      closeDelay = closeMoment.diff(new Date()),
      openDelay = openMoment.diff(new Date()),
      openSoon = openDelay <= 60 * 60 * 1000 && openDelay > 0, // ouvre dans 60 minutes
      closeSoon = closeDelay <= 60 * 60 * 1000 && closeDelay > 0; // ferme dans 60 minutes
    return {
      closed: false,
      openDatetime,
      openMoment,
      opened: moment().diff(openDatetime) > 0, // test si la boutique est actuellement ouverte
      closeSoon,
      closeMoment,
      openSoon,
      openDelay,
      moment: momentConversion,
      momentFormatted: momentConversion.format("LLLL"),
      now: now,
      diff: momentConversion.diff(targetDatetime),
    };
  }

  // getZonesDatetime(datetime: Date = new Date()) {
  //   // retourne l’ensemble des districts et postcodes déservis dans l'horaire et jour désiré
  //
  //   const store = this,
  //     from_to_exp = /^(.*)-(.*)$/,
  //     factorized_options = store.get("factorized_options"),
  //     startOfDay = moment(datetime).startOf("day"),
  //     startOfDayDate = startOfDay.format("YYYY-MM-DD "),
  //     minimum_shipping: number =
  //       factorized_options && factorized_options.minimum_shipping
  //         ? +factorized_options.minimum_shipping
  //         : 0;
  //   let zones: Array<{
  //     id_district?: string;
  //     id_postcode?: string;
  //     minimum_shipping: number;
  //   }> = [];
  //   _(factorized_options || {}).each(function (weekDays, timesKey) {
  //     const match = from_to_exp.test(timesKey);
  //     if (!match) return;
  //
  //     const e = from_to_exp.exec(timesKey);
  //
  //     if (e && Array.isArray(weekDays)) {
  //       const from = moment(startOfDayDate + e[1]),
  //         to = moment(startOfDayDate + e[2]);
  //       //
  //       if (from.isBefore(datetime) && to.isAfter(datetime)) {
  //         // on fait une union car potentiellement, plusieurs crénaux horaires
  //         // peuvent être valides
  //         zones = _.union(zones, weekDays[datetime.getDay()]);
  //       }
  //     }
  //   });
  //   //
  //   // cas de factorisation
  //   if (minimum_shipping) {
  //     const postcodes = store.getPostcodes();
  //     zones = postcodes.map((postcode) => {
  //       return {
  //         id_postcode: postcode,
  //         minimum_shipping: minimum_shipping,
  //       };
  //     });
  //   }
  //
  //   return zones;
  // }

  // getMinimumShippingPrice(
  //   datetime: Date,
  //   id_postcode?: string,
  //   id_district?: string
  // ) {
  //   //
  //   // retourne le prix minimum de livraison pour une date donnée
  //   //
  //   const store = this,
  //     factorized_options = store.get("factorized_options");
  //   let minimum_shipping: string | number = 0;
  //
  //   if (factorized_options && factorized_options.minimum_shipping) {
  //     minimum_shipping = factorized_options.minimum_shipping;
  //   } else {
  //     const zones = store.getZonesDatetime(datetime);
  //     if (id_district) {
  //       const district = _(zones).findWhere({
  //         id_district: id_district,
  //       });
  //       if (district) {
  //         minimum_shipping = district.minimum_shipping;
  //       }
  //     } else if (id_postcode) {
  //       const postcode = _(zones).findWhere({
  //         id_postcode: id_postcode,
  //       });
  //       if (postcode) {
  //         minimum_shipping = postcode.minimum_shipping;
  //       }
  //     }
  //   }
  //   return parseFloat(minimum_shipping + "");
  // }

  isAvailableNow(isVAE: boolean) {
    return this.findAvailableDatetime(isVAE).now === 1;
  }

  getPaymentGroups(): PaymentGroupCollection {
    const store = this,
      id_store = store.id;
    let paymentGroupCollection: PaymentGroupCollection =
      store.paymentGroupCollection;
    if (!paymentGroupCollection || !paymentGroupCollection.size()) {
      paymentGroupCollection = new PaymentGroupCollection();
      const globalPaymentGroupCollection = app.getPaymentGroups(),
        vae = store._storeContextModel?.get("vae") || false,
        modeProps = vae ? { vae: true } : { vad: true },
        paymentModesModifiers =
          store._storeContextModel?.get("payment_modes") || [];
      //
      globalPaymentGroupCollection?.each((globalPaymentGroup) => {
        const json = globalPaymentGroup.toJSON();
        paymentGroupCollection.add(
          new PaymentGroupModel({
            ...{ vae: false, vad: false, id_store },
            ...json,
          })
        );
      });
      paymentModesModifiers.forEach(function (modifier: {
        id_payment_mode: string | number; // retro-compatibilité projet ORM
        params?: any;
      }) {
        const id_payment_mode = modifier.id_payment_mode,
          basePaymentMode = app.getPaymentMode(String(id_payment_mode)),
          basePaymentModeJson = basePaymentMode?.toJSON() || {};
        // on merge les params
        modifier.params = {
          ...basePaymentModeJson.params,
          ...modifier.params,
        };
        const paymentMode = new PaymentModeModel({
          ...basePaymentModeJson,
          ...modifier,
          ...modeProps,
          id_store,
        });
        paymentGroupCollection.each((paymentGroup) => {
          if (
            paymentGroup
              .get("payment_modes")
              ?.map((id: string | number) => +id) // retro-compatibilité projet ORM
              .includes(+id_payment_mode) // retro-compatibilité projet ORM
          ) {
            paymentGroup.addPaymentMode(paymentMode);
          }
        });
      });
      const hasModifiers = paymentModesModifiers.length > 0;
      paymentGroupCollection.each((paymentGroup) => {
        // Avec storecontext : figer les groupes (même vides = magasin sans ce mode).
        // Sans payment_modes : ne pas figer isSetup — évite des groupes morts
        // tant que fetchStoreContext n’a pas sync.
        if (hasModifiers) {
          paymentGroup.isSetup = true;
        }
      });
      if (hasModifiers) store.paymentGroupCollection = paymentGroupCollection;
    }
    return paymentGroupCollection;
  }

  getStockoutProduct(): StockoutProductModel {
    const store = this;
    let stockoutProductModel = store.stockoutProduct;
    if (!stockoutProductModel) {
      stockoutProductModel = new StockoutProductModel({
        id_store: store.id,
      });
      store.stockoutProduct = stockoutProductModel;
    }
    return stockoutProductModel;
  }

  fetchStoreContext(
    done?: () => void,
    fail?: () => void
  ): StoreContextModel | void {
    const cart = app.getCart(),
      vae = cart.isVAE(),
      date = cart.isNow() ? undefined : cart.getDatetime();

    const customer = app.getCustomer();
    const rawSegmentation = customer.isComeIn()
      ? customer.getSegment()
      : undefined;
    const segmentation = rawSegmentation ? parseInt(rawSegmentation) : 0;

    // Attacher le contexte au store canonique (collection app) pour éviter
    // hasStoreContext() false sur une autre instance du même id.
    const store = (this.id && app.getStore(String(this.id))) || this;

    // Réutiliser un fetch en vol (mêmes params) — évite d'abort le sync qui commit les prix.
    const existingPending = store._pendingStoreContextModel;
    const wantMode = vae ? "vae" : "vad";
    const wantDateUrl = date
      ? date.toISOString().replace(/^(.{10})T((..):(..)).*/, "$1-$3-$4")
      : "now";
    if (
      existingPending &&
      !existingPending.get("_fetchFailed") &&
      `${existingPending.get("id_store")}` === `${store.id}` &&
      existingPending.get("mode") === wantMode &&
      existingPending.get("date_url") === wantDateUrl &&
      `${existingPending.get("segmentation")}` === `${segmentation}`
    ) {
      done && existingPending.once("sync", done);
      fail && existingPending.once("error", fail);
      return existingPending;
    }

    const storeContextModel = new StoreContextModel({
      id_store: `${store.id}`,
      date,
      vae,
      segmentation,
    });
    store.paymentGroupCollection = undefined;
    if (store._storeContextModelTimeout) {
      clearTimeout(store._storeContextModelTimeout);
      store._storeContextModelTimeout = undefined;
    }
    store._pendingStoreContextModel = storeContextModel;

    const onSync = () => {
      // Le listener est persistant (`on`) : il doit aussi traiter les syncs des refetch
      // d'expiration, quand `_pendingStoreContextModel` a déjà été libéré par le 1er sync.
      // On ignore uniquement les syncs d'un modèle supplanté par un contexte plus récent.
      const superseded = store._pendingStoreContextModel
        ? store._pendingStoreContextModel !== storeContextModel
        : !!store._storeContextModel &&
          store._storeContextModel !== storeContextModel;
      if (superseded) return;
      store._pendingStoreContextModel = undefined;
      store._storeContextRetried = false;
      store._storeContextModel = storeContextModel;
      // Miroir sur this si instance différente (sécurité).
      if (store !== this) {
        this._storeContextModel = storeContextModel;
        this._pendingStoreContextModel = undefined;
      }
      // Modes de paiement dépendent de payment_modes — invalider le cache groupes.
      store.paymentGroupCollection = undefined;
      const expire_date = storeContextModel.get("expire_date") || "";
      const expire_timeout = new Date(expire_date).getTime() - Date.now(),
        expire_timeout_max = Math.max(60 * 1000, expire_timeout);
      markStoreContextSynced(String(store.id), store);
      cart.trigger("changeContext");
      store._storeContextModelTimeout = setTimeout(() => {
        storeContextModel.fetch();
      }, expire_timeout_max);
    };

    const onError = () => {
      // Prix-FIX: libérer le pending mort sinon les prochains fetch sont coalescés à jamais.
      if (store._pendingStoreContextModel === storeContextModel) {
        store._pendingStoreContextModel = undefined;
      }
      storeContextModel.set({ _fetchFailed: true } as any, { silent: true });
      // Un retry auto si le 1er fetch échoue (évite hidePrice permanent).
      if (!store._storeContextRetried) {
        store._storeContextRetried = true;
        store.fetchStoreContext(done, fail);
      }
    };

    // Premier chargement uniquement : pas encore de prix magasin → on peut pointer le modèle vide.
    // Sinon on conserve l'ancien _storeContextModel jusqu'au sync (évite hidePrice).
    if (!store.hasStoreContext()) {
      store._storeContextModel = storeContextModel;
    }
    done && storeContextModel.once("sync", done);
    storeContextModel.on("sync", onSync);
    storeContextModel.once("error", onError);
    fail && storeContextModel.once("error", fail);

    // Prix-PERF: cache HTTP unique (vanilla / ModelProvider) puis apply Backbone.
    ensureStoreContextFetch({
      id_store: String(store.id),
      mode: wantMode,
      date_url: wantDateUrl,
      segmentation,
    });
    // @ts-ignore
    void applyPrefetchToStoreContextModel(storeContextModel).then((applied) => {
      if (!applied) {
        storeContextModel.fetch();
      }
    });
    return storeContextModel;
  }

  fetchStockoutProduct(done?: () => void, fail?: () => void) {
    const model = this;
    const stockoutProductModel = this.getStockoutProduct();
    done && model.listenToOnce(stockoutProductModel, "sync", done);
    fail && model.listenToOnce(stockoutProductModel, "error", fail);
    stockoutProductModel.fetch();
    return stockoutProductModel;
  }

  async fetchClosure() {
    const store = this;
    const closure = await ApiRequest(
      "StoreClosure",
      {},
      { id_store: store.getId() },
      { type: "GET" }
    );
    store.set("closure", closure);
    return closure;
  }

  // getPaymentMode(id_payment_mode: string | number) {
  //   const paymentGroups = this.getPaymentGroups();
  //   return paymentGroups.getPaymentMode(id_payment_mode);
  // }

  getPaymentModes() {
    const paymentGroupCollection = this.getPaymentGroups();
    return paymentGroupCollection.getPaymentModes();
  }

  getCartDeliveryOptions(): ICartDeliveryOptionsVAE {
    return {
      vae: 1,
      id_store: this.getId(),
    };
  }

  getTimes(
    isVAE: boolean,
    targetDatetime?: Date,
    forceAm: boolean = false,
    forceAmPm: boolean = false
  ) {
    //
    // retourne les horaires de la boutique, en fonction de VAE, et de la date cible
    //
    const periodHours = this.getTimePeriods(isVAE, targetDatetime, false, true);
    const isAm = !!forceAm || this.isDatetimeAm(isVAE, targetDatetime),
      periods = periodHours
        ? forceAmPm
          ? [periodHours.am, periodHours.pm]
          : isAm
          ? [periodHours.am]
          : [periodHours.pm]
        : [];

    const hoursArray: {
      label: string;
      h: number;
      m: number;
      isAvailable: boolean;
      isNow: boolean;
      date: Date;
    }[] = [];
    if (periodHours) {
      periods.forEach((period) => {
        const debDate = period.deb,
          endDate = period.end,
          periodAvailable = !(period.closed || period.passed),
          now = moment(new Date()),
          h = new Date(debDate);
        let nowExists = false;

        // délai maximum pour différer une commande : 30 minutes
        now.add(0, "minutes");
        while (moment(endDate).diff(h) >= 0) {
          let nowDiff = now.diff(h),
            momentH = moment(h),
            isPassed = nowDiff > -minDifferDelay(),
            isNow = nowDiff < 0 && nowDiff > -minDifferDelay(); //(!nowExists) &&
          nowExists ||= isNow;
          const data = {
            label: momentH.format("LT"),
            h: momentH.hours(),
            m: momentH.minutes(),
            isAvailable: periodAvailable && nowDiff < 0,
            isNow: isNow,
            date: h,
          };
          if (!(forceAmPm && isPassed)) {
            hoursArray.push(data);
          }
          h.setMinutes(h.getMinutes() + 15);
        }
      });
    }
    return hoursArray;
  }

  getTimesOptions(isVAE: boolean, targetDay?: Date) {
    const now = new Date();
    const isToday = moment(targetDay).isSame(now, "d");
    const isNowAvailable = isToday && this.isAvailableNow(isVAE);
    const differTimes = this.getTimes(isVAE, targetDay, false, true);
    const timesOptions = [];
    if (isToday) {
      timesOptions.push({
        value: "now",
        label: app.t("Maintenant", "Datetime"),
        disabled: !isNowAvailable,
      });
    }
    differTimes.forEach(({ h, m, label }) => {
      const disabled =
        isToday && moment().set("h", h).set("m", m).diff(now) < 0;
      const HH = `${h}`.padStart(2, "0");
      const MM = `${m}`.padStart(2, "0");
      timesOptions.push({
        value: `${HH}:${MM}`,
        label,
        disabled,
      });
    });
    return timesOptions;
  }

  nextOpeningTime(date: Date = new Date(), isVAE: boolean): Date | null {
    const store = this;
    const timePeriods = store.getTimePeriods(isVAE, date);
    const pmDeb = timePeriods?.pm?.deb;
    const amDeb = timePeriods?.am?.deb;
    let nextOpeningTime: Date | null = null;

    if (pmDeb && moment(pmDeb).isAfter(moment(date))) {
      nextOpeningTime = moment(pmDeb).toDate();
    } else if (amDeb && moment(amDeb).isAfter(moment(date))) {
      nextOpeningTime = moment(amDeb).toDate();
    } else {
      nextOpeningTime = moment(amDeb).add(1, "day").toDate();
    }
    return nextOpeningTime;
  }

  getEndOfService(date: Date = new Date(), isVAE: boolean): Date | null {
    const store = this;
    let endDate: Date | null = null;

    // First check exceptional closure
    const closure = store.getClosure();
    if (closure && closure.closed) {
      return null;
    }

    const timePeriods = store.getTimePeriods(isVAE, date);

    const todayHours = {
      time_closing_am: timePeriods?.am?.closing,
      time_opening_pm: timePeriods?.pm?.deb,
      time_closing_pm: timePeriods?.pm?.closing,
    };

    // Check if continuous service
    const isContinuousService = moment(todayHours.time_closing_am).isSame(
      moment(todayHours.time_opening_pm)
    );

    if (isContinuousService) {
      // For continuous service, always return PM end
      endDate = todayHours.time_closing_pm || null;
    } else {
      // For split service, check current time against AM end
      const now = moment(date);
      const amEnd = moment(todayHours.time_closing_am || "00:00");

      // If current time is after AM end, return PM end, otherwise return AM end
      endDate = now.isAfter(amEnd)
        ? moment(todayHours.time_closing_pm || "00:00").toDate()
        : amEnd.toDate();
    }

    if (closure && !closure.closed && closure.next_date) {
      const closureDate = moment(closure.next_date).toDate();
      if (endDate) {
        if (closureDate.getTime() < endDate.getTime()) {
          endDate = closureDate;
        }
      } else {
        endDate = closureDate;
      }
    }

    return endDate;
  }

  // setDeliveryAreas() {
  //   const model = this;
  //   let deliveryAreasGeoJson = model.get("deliveryAreas") || [];
  //   // cette fonction est gourmande en calcul, on met les données en cache.
  //   if (!deliveryAreasGeoJson) {
  //     let deliveryAreasBbox;
  //     const deliveryAreasPolygons: IMultiPolygon[] = [];
  //     const indexed = {};
  //
  //     // on utilise le panier comme référence temporelle.
  //     const zones = model.getZonesDatetime(app.getCart().getDatetime());
  //
  //     zones.forEach(function (zone) {
  //       let polygons: IMultiPolygon | undefined;
  //       let obj,
  //         id_obj = zone.id_district;
  //       if (zone.id_district) {
  //         obj = app.getDistrict(id_obj);
  //         if (obj && obj.polygons) {
  //           polygons = obj.polygons;
  //         }
  //       } else if ((id_obj = zone.id_postcode)) {
  //         obj = app.getPostcode(id_obj);
  //         if (obj) {
  //           polygons = obj.getPolygons();
  //         }
  //       }
  //       if (!!polygons) {
  //         deliveryAreasPolygons.push(polygons);
  //       }
  //     });
  //
  //     // TODO: deliveryAreasGeoJson
  //     // deliveryAreasGeoJson = model.parseLatLngPolygonsToGeoJson(
  //     //   deliveryAreasPolygons
  //     // );
  //     // deliveryAreasBbox = model.getBoundingBox(deliveryAreasGeoJson);
  //
  //     // deliveryAreasGeoJson.features.forEach(function (item) {
  //     //   indexed[model.keyFor(item)] = item;
  //     // });
  //
  //     // var coords = Object.keys(indexed).map(function (k) {
  //     //   return indexed[k];
  //     // });
  //
  //     // deliveryAreasGeoJson.features = coords;
  //
  //     // model.set({
  //     //   deliveryAreas: deliveryAreasGeoJson,
  //     //   deliveryAreasBbox: deliveryAreasBbox,
  //     // });
  //   }
  //   model.trigger("ready:deliveryAreas", model);
  // }

  // parseLatLngPolygonsToGeoJson(latLngPolygons: any[][]) {
  //   const geoJson: GeoJSONSourceOptionsData = {
  //     type: "FeatureCollection",
  //     features: [],
  //   };
  //   _.each(latLngPolygons, (a) => {
  //     _.each(a, (x) => {
  //       _.each(x, (b) => {
  //         const geometry: any = {
  //           type: "Polygon",
  //           coordinates: [[]],
  //         };
  //         _.each(b, (c) => {
  //           geometry.coordinates[0].push([c.lng, c.lat]);
  //         });
  //
  //         geoJson.features.push({
  //           type: "Feature",
  //           geometry,
  //         } as any);
  //       });
  //     });
  //   });
  //   return geoJson;
  // }

  // getBoundingBox(gj: object) {
  //   var coords, bbox;
  //   if (!gj.hasOwnProperty("type")) return;
  //   coords = this.getCoordinatesDump(gj);
  //   bbox = [
  //     Number.POSITIVE_INFINITY,
  //     Number.POSITIVE_INFINITY,
  //     Number.NEGATIVE_INFINITY,
  //     Number.NEGATIVE_INFINITY,
  //   ];
  //   return coords.reduce(function (prev: number[], coord: number[]) {
  //     return [
  //       Math.min(coord[0], prev[0]),
  //       Math.min(coord[1], prev[1]),
  //       Math.max(coord[0], prev[2]),
  //       Math.max(coord[1], prev[3]),
  //     ];
  //   }, bbox);
  // }

  // getCoordinatesDump(gj: any): any {
  //   let coords;
  //   const model = this,
  //     coordinates = gj.coordinates,
  //     geometries = gj.geometries,
  //     features = gj.features,
  //     geometry = gj.geometry,
  //     type = gj.type;
  //   if (type === "Point") {
  //     coords = [coordinates];
  //   } else if (type === "LineString" || type === "MultiPoint") {
  //     coords = coordinates;
  //   } else if (type === "Polygon" || type === "MultiLineString") {
  //     coords = coordinates.reduce(function (dump: string | any[], part: any) {
  //       return dump.concat(part);
  //     }, []);
  //   } else if (type === "MultiPolygon") {
  //     coords = coordinates.reduce(function (dump: string | any[], poly: any[]) {
  //       return dump.concat(
  //         poly.reduce(function (points, part) {
  //           return points.concat(part);
  //         }, [])
  //       );
  //     }, []);
  //   } else if (type === "Feature") {
  //     coords = model.getCoordinatesDump(geometry);
  //   } else if (type === "GeometryCollection") {
  //     coords = geometries.reduce(function (dump: string | any[], g: any) {
  //       return dump.concat(model.getCoordinatesDump(g));
  //     }, []);
  //   } else if (type === "FeatureCollection") {
  //     coords = features.reduce(function (dump: string | any[], f: any) {
  //       return dump.concat(model.getCoordinatesDump(f));
  //     }, []);
  //   }
  //   return coords;
  // }

  recursiveFix(coords: any[]) {
    var model = this;
    if (coords instanceof Array) {
      coords.forEach(function (coord, i) {
        if (
          coords[i] instanceof Array &&
          coords[i].length > 0 &&
          coords[i][0] instanceof Array
        ) {
          coords[i] = model.recursiveFix(coord);
        } else if (i < coords.length - 1) {
          /* while (
                      coords[i][0] === coords[i + 1][0] &&
                      coords[i][1] === coords[i + 1][1]
                    ) {
                    } */
        }
      });

      return coords;
    }
  }

  // findDuplicates(coords: any) {
  //   const flattened = _.flatten(coords, true);
  //   const repeats: any[] = [];
  //
  //   do {
  //     const point = flattened.shift();
  //
  //     _.each(flattened, function (testPoint) {
  //       if (testPoint[0] === point[0] && testPoint[1] === point[1]) {
  //         repeats.push(point);
  //       }
  //     });
  //   } while (flattened.length > 0);
  //
  //   return repeats;
  // }

  // keyFor(item: { geometry: { coordinates: any } }) {
  //   const coordinates = item.geometry.coordinates;
  //   return coordinates[0] + ":" + coordinates[1];
  // }
  //
  // getCornerLandingLink() {
  //   return app.c("_CORNER_LANDING_LINK_");
  // }

  // getAvisVerifie() {
  //   const store = this;
  //   let avisVerifie = store.avisVerifie;
  //   if (!avisVerifie) {
  //     avisVerifie = new StoreAvisVerifieModel({
  //       id_store: store.id,
  //     });
  //     store.avisVerifie = avisVerifie;
  //   }
  //   return avisVerifie;
  // }

  // fetchAvisVerifie(done: () => void, fail: () => void) {
  //   var storeAvisVerifieModel = this.getAvisVerifie();
  //   storeAvisVerifieModel.once("sync", done).once("error", fail);
  //   storeAvisVerifieModel.fetch();
  //   return storeAvisVerifieModel;
  // }

  // lastAvisVerifie() {
  //   var avis = this.getAvisVerifie();
  //   if (avis) {
  //     return _(avis.getComments());
  //   }
  //
  //   return _([]);
  // }

  // getAvisStates() {
  //   var avis = this.getAvisVerifie();
  //   if (avis) {
  //     return avis.getStates();
  //   }
  //
  //   return {
  //     1: "0",
  //     2: "0",
  //     3: "0",
  //     4: "0",
  //     5: "0",
  //   };
  // }
  //
  // getAvisCertificateUrl() {
  //   var avis = this.getAvisVerifie();
  //   if (avis) {
  //     return avis.getCertificateUrl();
  //   }
  //
  //   return "";
  // }
}

StoreModel.prototype.name = "StoreModel";
StoreModel.prototype.route = "apiStoreModel";
StoreModel.prototype.idAttribute = "id_store";
StoreModel.prototype.url = function () {
  return urlWithSyncVersion(`/store/${this.id}`);
};
StoreModel.prototype.remoteCache = "no-cache";
// @ts-ignore
export default StoreModel;
